import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Preferences } from '@capacitor/preferences'
import type { CustomChart } from '../types/customCharts'

const STORAGE_KEY = 'go_manager_custom_charts'

export const useCustomChartsStore = defineStore('customCharts', () => {
  // État
  const charts = ref<CustomChart[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)

  // Actions
  const loadCharts = async () => {
    try {
      isLoading.value = true
      error.value = null

      const { value } = await Preferences.get({ key: STORAGE_KEY })
      if (value) {
        const parsed = JSON.parse(value)
        // Conversion des dates string en objets Date
        charts.value = parsed.map((chart: any) => ({
          ...chart,
          createdAt: new Date(chart.createdAt),
          updatedAt: new Date(chart.updatedAt)
        }))
      }
      isInitialized.value = true
    } catch (err) {
      error.value = 'Erreur lors du chargement des graphiques'
      console.error('Error loading charts:', err)
      isInitialized.value = true
    } finally {
      isLoading.value = false
    }
  }

  // const loadChartsForReport = async (centerId: number, reportId: string) => {
  //   await loadCharts() // Charger tous puis filtrer
  // }

  const saveToStorage = async () => {
    try {
      await Preferences.set({ 
        key: STORAGE_KEY, 
        value: JSON.stringify(charts.value)
      })
    } catch (err) {
      error.value = 'Erreur lors de la sauvegarde'
      console.error('Error saving charts:', err)
    }
  }

  const addChart = async (chart: CustomChart) => {
    // Vérifier que l'ID n'existe pas déjà
    const exists = charts.value.some(c => c.id === chart.id)
    if (exists) {
      chart.id = generateUniqueId()
    }

    // Initialiser l'état si manquant
    if (!chart.state) {
      chart.state = {
        loading: false,
        syncStatus: 'synced',
        lastUpdated: new Date()
      }
    }

    charts.value.unshift(chart) // Ajouter au début de la liste
    await saveToStorage()
  }

  const updateChart = async (updatedChart: CustomChart) => {
    const index = charts.value.findIndex(c => c.id === updatedChart.id)
    if (index !== -1) {
      charts.value[index] = updatedChart
      await saveToStorage()
    }
  }

  const deleteChart = async (chartId: string) => {
    const index = charts.value.findIndex(c => c.id === chartId)
    if (index !== -1) {
      charts.value.splice(index, 1)
      await saveToStorage()
    }
  }

  const getChart = (chartId: string): CustomChart | undefined => {
    return charts.value.find(c => c.id === chartId)
  }

  const duplicateChart = async (chartId: string) => {
    const original = getChart(chartId)
    if (original) {
      const duplicate: CustomChart = {
        ...original,
        id: generateUniqueId(),
        name: original.name + ' (Copie)',
        createdAt: new Date(),
        updatedAt: new Date()
      }
      await addChart(duplicate)
      return duplicate
    }
  }

  // Export/Import (pour futures extensions)
  const exportCharts = (): string => {
    return JSON.stringify(charts.value, null, 2)
  }

  const importCharts = async (jsonData: string) => {
    try {
      const imported = JSON.parse(jsonData)
      if (Array.isArray(imported)) {
        for (const chart of imported) {
          // Générer de nouveaux IDs pour éviter les conflits
          chart.id = generateUniqueId()
          chart.createdAt = new Date(chart.createdAt)
          chart.updatedAt = new Date(chart.updatedAt)
          await addChart(chart)
        }
        return true
      }
    } catch (err) {
      error.value = 'Format de fichier invalide'
      console.error('Error importing charts:', err)
      return false
    }
    return false
  }

  // Getters calculés
  const chartsByType = (type: string) => {
    return charts.value.filter(chart => chart.config.chartType === type)
  }

  const getChartsForReport = async (centerId: number, reportId: string) => {
    if (!isInitialized.value) {
      await loadCharts()
    }
    return charts.value.filter(chart => 
      chart.centerId === centerId && chart.reportId === reportId
    )
  }

  const recentCharts = (limit: number = 5) => {
    return charts.value
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
      .slice(0, limit)
  }

  // Utilitaires
  const generateUniqueId = (): string => {
    let id: string
    do {
      id = 'chart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    } while (charts.value.some(c => c.id === id))
    return id
  }

  const clearError = () => {
    error.value = null
  }

  // Statistiques (pour future dashboard)
  const getStats = () => {
    return {
      total: charts.value.length,
      byType: charts.value.reduce((acc, chart) => {
        acc[chart.config.chartType] = (acc[chart.config.chartType] || 0) + 1
        return acc
      }, {} as Record<string, number>),
      lastCreated: charts.value.length > 0 
        ? Math.max(...charts.value.map(c => c.createdAt.getTime()))
        : null
    }
  }

  return {
    // État
    charts,
    isLoading,
    error,

    // Actions
    loadCharts,
    addChart,
    updateChart,
    deleteChart,
    getChart,
    duplicateChart,
    exportCharts,
    importCharts,
    clearError,

    // Getters
    chartsByType,
    getChartsForReport,
    recentCharts,
    getStats
  }
})